/**
 * Normalize DB rows into a single shape for {@link ProjectDetailRich}.
 * Image fields are HTTPS URLs only (Supabase Storage, R2, CDN, etc.).
 */

export function parseLineSeparatedUrls(text) {
  return String(text || '')
    .split(/[\r\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

export function emptyDisplayAssets() {
  return {
    hero_image_urls: [],
    wip_image_urls: [],
    moodboard_image_urls: [],
    key_statements: [],
    key_elements: [],
    mind_map: { nodes: [], edges: [] },
    model_url: '',
    credits: { client_name: '', project_date: '', participants: '' },
  }
}

export function normalizeDisplayAssets(raw) {
  const d = raw && typeof raw === 'object' ? raw : {}
  const hero = Array.isArray(d.hero_image_urls) ? d.hero_image_urls : []
  const wip = Array.isArray(d.wip_image_urls) ? d.wip_image_urls : []
  const mood = Array.isArray(d.moodboard_image_urls) ? d.moodboard_image_urls : []
  const ks = Array.isArray(d.key_statements) ? d.key_statements : []
  const ke = Array.isArray(d.key_elements) ? d.key_elements : []
  const mm = d.mind_map && typeof d.mind_map === 'object' ? d.mind_map : { nodes: [], edges: [] }
  const credits = d.credits && typeof d.credits === 'object' ? d.credits : {}
  return {
    hero_image_urls: hero.map(String).filter(Boolean),
    wip_image_urls: wip.map(String).filter(Boolean),
    moodboard_image_urls: mood.map(String).filter(Boolean),
    key_statements: ks.map((x) => ({
      title: typeof x?.title === 'string' ? x.title : '',
      content: typeof x?.content === 'string' ? x.content : typeof x === 'string' ? x : '',
    })),
    key_elements: ke.map((x) => ({
      title: typeof x?.title === 'string' ? x.title : '',
      description: typeof x?.description === 'string' ? x.description : typeof x === 'string' ? x : '',
    })),
    mind_map: {
      nodes: Array.isArray(mm.nodes) ? mm.nodes : [],
      edges: Array.isArray(mm.edges) ? mm.edges : [],
    },
    model_url: typeof d.model_url === 'string' ? d.model_url : '',
    credits: {
      client_name: typeof credits.client_name === 'string' ? credits.client_name : '',
      project_date: typeof credits.project_date === 'string' ? credits.project_date : '',
      participants: typeof credits.participants === 'string' ? credits.participants : '',
    },
  }
}

/** Hub RPC / share payload */
export function normalizeHubPublicProject(raw) {
  if (!raw) return null
  const d = normalizeDisplayAssets(raw.display_assets)
  const sch = raw.schedule && typeof raw.schedule === 'object' ? raw.schedule : {}
  const hasSchedule =
    Boolean(sch.deadline) ||
    Boolean(sch.timeframe) ||
    (Array.isArray(sch.phases) && sch.phases.length > 0)

  return {
    title: raw.name || '',
    category: raw.category || '',
    excerpt: raw.description || '',
    vision: raw.vision || '',
    heroImages: d.hero_image_urls,
    wipImages: d.wip_image_urls,
    moodboardImages: d.moodboard_image_urls,
    keyStatements: d.key_statements,
    keyElements: d.key_elements,
    mindMap: d.mind_map,
    modelUrl: d.model_url || '',
    schedule: sch,
    showTimeline: hasSchedule,
    credits: d.credits,
    status: raw.status || '',
  }
}

/** portfolio_projects row (legacy / rich columns when present) */
export function normalizePortfolioProject(p) {
  if (!p) return null

  const heroImages = []
  if (Array.isArray(p.hero_images) && p.hero_images.length > 0) {
    for (const img of p.hero_images) {
      const u = typeof img === 'string' ? img : img?.url
      if (u) heroImages.push(u)
    }
  } else if (p.hero_image_url) {
    heroImages.push(p.hero_image_url)
  } else if (p.thumbnail_url) {
    heroImages.push(p.thumbnail_url)
  }

  const wipImages = Array.isArray(p.wip_images)
    ? p.wip_images.map((img) => (typeof img === 'string' ? img : img?.url)).filter(Boolean)
    : []

  const moodboardImages = Array.isArray(p.moodboard_images)
    ? p.moodboard_images.map((img) => (typeof img === 'string' ? img : img?.url)).filter(Boolean)
    : []

  const keyStatements = Array.isArray(p.key_statements) ? p.key_statements : []
  const keyElements = Array.isArray(p.key_elements) ? p.key_elements : []
  const mindMap = p.mind_map && typeof p.mind_map === 'object' ? p.mind_map : { nodes: [], edges: [] }

  let participantsStr = ''
  if (Array.isArray(p.participants)) {
    participantsStr = p.participants.filter(Boolean).join(', ')
  } else if (typeof p.participants === 'string') {
    participantsStr = p.participants.trim()
  }

  return {
    title: p.title || '',
    category: p.category || '',
    excerpt: p.excerpt || '',
    vision: '',
    heroImages,
    wipImages,
    moodboardImages,
    keyStatements,
    keyElements,
    mindMap: { nodes: mindMap.nodes || [], edges: mindMap.edges || [] },
    modelUrl: p.model_url || '',
    schedule: null,
    showTimeline: p.status === 'in_development',
    credits: {
      client_name: typeof p.client_name === 'string' ? p.client_name : '',
      project_date: p.project_date || '',
      participants: participantsStr,
    },
    status: p.status || '',
  }
}
